import React, { useState, useEffect } from 'react';
import { Box, Alert, Container, Tabs, Tab } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { DataManagementTab } from '@shared/components/data-management';
import { createActiveSurveysConfig } from '../config/activeSurveysConfig';
import { createDraftSurveysConfig } from '../config/draftSurveysConfig';
import { createClosedSurveysConfig } from '../config/closedSurveysConfig';
import { useCloseSurvey, useReopenSurvey, usePublishSurvey } from '../hooks/useSurveys';
import { useContextStore } from '../../../stores/contextStore';
import { useSetPageActions } from '../../../contexts/PageActionsContext';

export const UnifiedSurveyManagement: React.FC = () => {
  const navigate = useNavigate();
  const { activeTenantId, activeSubjectId, activeDomainId } = useContextStore();
  const [activeTab, setActiveTab] = useState<0 | 1 | 2>(0); // 0 = Active (default)
  const { setHelp } = useSetPageActions();

  useEffect(() => {
    setHelp({
      title: 'Survey Management Guide',
      sections: [
        {
          title: 'Active',
          content:
            'Published surveys that are actively accepting responses. You can create instances, edit questions, or close surveys.',
        },
        {
          title: 'Draft',
          content:
            'Surveys that are still being edited and not yet published. You can edit questions, publish to make them active, or close draft surveys.',
        },
        {
          title: 'Closed',
          content:
            'Surveys that have been closed and are no longer accepting responses. You can reopen them to draft status or delete them if they have no instances.',
        },
      ],
    });
  }, [setHelp]);

  // Get mutation hooks for close/reopen/publish actions
  const closeSurveyMutation = useCloseSurvey(activeSubjectId || '');
  const reopenSurveyMutation = useReopenSurvey(activeSubjectId || '');
  const publishSurveyMutation = usePublishSurvey(activeSubjectId || '');

  // Tab change handler
  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue as 0 | 1 | 2);
  };

  // Handle no subject selected state
  if (!activeSubjectId || !activeTenantId || !activeDomainId) {
    return (
      <Container
        maxWidth={false}
        disableGutters
        sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
      >
        <Alert severity="info">
          Please select a subject to view surveys.
        </Alert>
      </Container>
    );
  }

  // Create the three survey configs
  const activeSurveysConfig = createActiveSurveysConfig(
    activeSubjectId,
    activeTenantId,
    activeDomainId,
    navigate,
    closeSurveyMutation
  );

  const draftSurveysConfig = createDraftSurveysConfig(
    activeSubjectId,
    activeTenantId,
    activeDomainId,
    navigate,
    closeSurveyMutation,
    publishSurveyMutation
  );

  const closedSurveysConfig = createClosedSurveysConfig(
    activeSubjectId,
    activeTenantId,
    activeDomainId,
    navigate,
    reopenSurveyMutation
  );

  return (
    <Container
      maxWidth={false}
      disableGutters
      sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}
    >
      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1.5, flexShrink: 0 }}>
        <Tabs value={activeTab} onChange={handleTabChange}>
          <Tab label="Active" />
          <Tab label="Draft" />
          <Tab label="Closed" />
        </Tabs>
      </Box>

      {/* Tab Content */}
      <Box sx={{ flex: 1, minHeight: 0 }}>
        {activeTab === 0 && <DataManagementTab config={activeSurveysConfig} />}
        {activeTab === 1 && <DataManagementTab config={draftSurveysConfig} />}
        {activeTab === 2 && <DataManagementTab config={closedSurveysConfig} />}
      </Box>
    </Container>
  );
};
